import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useListMap from "../hooks/useListMap";
import {
  TitleCategory,
  ItemMap,
  TitleItem,
  DescriptionItem,
  ImgItem,
  ImgContentItem,
  TitlePage,
  MapContent,
  MainContent,
  FluidContent,
  ImageModalOverlay,
  ImageModalContent,
  ImageModalClose,
  MobileCategoryTabs,
  MobileCategoryTab,
  MobileCategoryHeader,
  MobileCategoryLine,
  MobileCategoryTitle,
  MobileCategoryCount,
  MobileStickyCategories,
  MobileListContainer,
  MobileItemsScroll,
  MobileItemsList,
  MobileItemCard,
  MobileItemImage,
  MobileItemBody,
  MobileItemTitleRow,
  MobileItemIndex,
  MobileItemTitle,
  MobileItemBusiness,
  MobileItemActions,
  MobileDetailSection,
  MobileDetailMedia,
  MobileDetailIndex,
  MobileDetailText,
  MobilePinModalOverlay,
  MobilePinModalContent,
  MobilePinModalHandle,
  MobilePinModalHeader,
  MobilePinModalTitleGroup,
  MobilePinModalIndex,
  MobilePinModalTitle,
  MobilePinModalBusiness,
  MobilePinModalClose,
  MobilePinModalDescription,
} from "../components/styled/ItemsMap";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { MapKey } from "../services/appenv";
import {
  BiSolidRightArrow,
  BiX,
} from "react-icons/bi";
import { getImageFromDrive } from "../services/api";
import { StickyContainer, Sticky } from "react-sticky";
import Collapse from "react-collapse";

const getMapStyles = (isMobile) => ({
  width: "100%",
  position: isMobile ? "relative" : "sticky",
  top: isMobile ? 0 : "70px",
  right: isMobile ? "auto" : 0,
  zIndex: "1",
  height: isMobile ? "100%" : "calc(100vh - 180px)",
});


const MapInner = React.memo(
  ({ location, zoom, animation, isMobile, markers, markersKey, onMarkerClick, onMapLoad }) => {
    return (
      <GoogleMap
        mapContainerStyle={getMapStyles(isMobile)}
        zoom={zoom}
        center={location}
        onLoad={onMapLoad}
      options={{
        mapTypeControl: false,
        gestureHandling: "greedy",
      }}
      >
        {(markers || []).map((item, index) => (
          <Marker
            key={`${markersKey || "pin"}-${index}`}
            position={{ lat: item.lat, lng: item.lng }}
            animation={animation}
            onLoad={(marker) => {
              marker.addListener("click", () => {
                if (onMarkerClick) {
                  onMarkerClick(item, index);
                }
              });
            }}
            onUnmount={(marker) => {
              if (window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(marker);
              }
            }}
            label={
              isMobile
                ? {
                  text: String(index + 1),
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: "700",
                }
                : undefined
            }
          />
        ))}
      </GoogleMap>
    );
  }
);

const MapLoader = (props) => (
  <LoadScript googleMapsApiKey={MapKey}>
    <MapInner {...props} />
  </LoadScript>
);

const TokyoMaps = () => {
  const ListMapData = useListMap();
  const mobileTabsRef = useRef(null);
  const mobileTabRefs = useRef({});
  const [location, setLocation] = useState({
    lat: 35.66196799,
    lng: 139.7017574,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [isTabsFixed, setIsTabsFixed] = useState(false);
  const initialZoom = 15;
  const [zoom, setZoom] = useState(initialZoom);

  const [openIndexes, setOpenIndexes] = useState([]);
  const [animation, setAnimation] = useState("");
  const [selectedPinItem, setSelectedPinItem] = useState(null);
  const mapInstanceRef = useRef(null);
  const lastFittedCategoryRef = useRef("");
  const activeMarkersRef = useRef([]);
  const activeCategoryRef = useRef("");
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsTabsFixed(false);
      return undefined;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const mapHeight = window.innerHeight * 0.7;
      const threshold = Math.max(mapHeight - 80, 0);
      setIsTabsFixed(scrollY > threshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMobile]);

  const handleButtonClick = (lat, lng) => {
    setZoom(15);
    setLocation({ lat: lat, lng: lng });

    setTimeout(() => {
      setAnimation(null);
    }, 1000);
  };
  const handleColapseItem = (index, category) => {
    const indexAndCategory = { index, category };
    if (
      openIndexes.some(
        (item) => item.index === index && item.category === category
      )
    ) {
      setOpenIndexes(
        openIndexes.filter(
          (item) => !(item.index === index && item.category === category)
        )
      );
    } else {
      setOpenIndexes([...openIndexes, indexAndCategory]);
    }
  };

  const fitMapToMarkers = (markers, map = mapInstanceRef.current) => {
    if (!map || !window.google || !markers || markers.length === 0) {
      return;
    }

    if (markers.length === 1) {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      map.setZoom(15);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((item) => {
      bounds.extend({ lat: item.lat, lng: item.lng });
    });
    map.fitBounds(bounds);
  };

  const handleMapLoad = useCallback(
    (map) => {
      mapInstanceRef.current = map;

      if (isMobile && activeMarkersRef.current.length > 0) {
        lastFittedCategoryRef.current = activeCategoryRef.current;
        fitMapToMarkers(activeMarkersRef.current, map);
      }
    },
    [isMobile]
  );

  const handleMarkerClick = useCallback((item, index) => {
    setSelectedPinItem({ ...item, index });
  }, []);

  const handleClosePinModal = () => {
    setSelectedPinItem(null);
  };

  const organizedData = useMemo(() => {
    if (!ListMapData?.allTokyoMapsListMap?.nodes) {
      return [];
    }

    const sortedNodes = [...ListMapData.allTokyoMapsListMap.nodes].sort(
      (a, b) => {
        const orderA = parseInt(a.order, 10) || 0;
        const orderB = parseInt(b.order, 10) || 0;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        const titleOrderA = parseInt(a.orderByTitle, 10) || 0;
        const titleOrderB = parseInt(b.orderByTitle, 10) || 0;

        return titleOrderA - titleOrderB;
      }
    );

    return sortedNodes.reduce((acc, item) => {
      const category = item.category.toLowerCase();
      const existingEntry = acc.find((entry) => entry.category === category);
      const normalizedItem = {
        description: item.description,
        title: item.title,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lng),
        image1: item.image1,
        image2: item.image2,
        bussiness: item.bussiness,
        businessCategory: item.businessCategory,
        color: item.color,
      };

      if (existingEntry) {
        existingEntry.data.push(normalizedItem);
      } else {
        acc.push({
          category: category,
          data: [normalizedItem],
        });
      }
      return acc;
    }, []);
  }, [ListMapData]);

  const categoriesForTabs = useMemo(() => {
    if (!organizedData.length) {
      return [];
    }

    const allItems = organizedData.flatMap((entry) => entry.data);
    return [{ category: "all", data: allItems }, ...organizedData];
  }, [organizedData]);

  const activeMarkers = useMemo(() => {
    if (!activeCategory || !categoriesForTabs.length) {
      return [];
    }

    const categoryEntry = categoriesForTabs.find(
      (entry) => entry.category === activeCategory
    );

    return categoryEntry ? categoryEntry.data : [];
  }, [activeCategory, categoriesForTabs]);

  useEffect(() => {
    activeMarkersRef.current = activeMarkers;
  }, [activeMarkers]);

  useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);

  const desktopMarker = useMemo(
    () => [{ lat: location.lat, lng: location.lng }],
    [location.lat, location.lng]
  );

  const mobileMapCenter = useMemo(() => {
    if (activeMarkers.length === 0) {
      return location;
    }

    const totalLat = activeMarkers.reduce((sum, item) => sum + item.lat, 0);
    const totalLng = activeMarkers.reduce((sum, item) => sum + item.lng, 0);
    return {
      lat: totalLat / activeMarkers.length,
      lng: totalLng / activeMarkers.length,
    };
  }, [activeMarkers, location]);

  useEffect(() => {
    if (!categoriesForTabs.length) {
      return;
    }

    const hasActiveCategory = categoriesForTabs.some(
      (entry) => entry.category === activeCategory
    );

    if (!activeCategory || !hasActiveCategory) {
      setActiveCategory("all");
    }
  }, [activeCategory, categoriesForTabs]);

  useEffect(() => {
    if (
      !isMobile ||
      !mapInstanceRef.current ||
      !activeCategory ||
      lastFittedCategoryRef.current === activeCategory
    ) {
      return;
    }

    lastFittedCategoryRef.current = activeCategory;
    fitMapToMarkers(activeMarkers, mapInstanceRef.current);
  }, [activeCategory, activeMarkers, isMobile]);

  useEffect(() => {
    if (!isMobile || categoriesForTabs.length <= 5) {
      return;
    }

    const tabsContainer = mobileTabsRef.current;
    const activeIndex = categoriesForTabs.findIndex(
      (category) => category.category === activeCategory
    );

    if (!tabsContainer || activeIndex === -1) {
      return;
    }

    const activeTabNode = mobileTabRefs.current[activeCategory];

    if (!activeTabNode) {
      return;
    }

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTabNode.getBoundingClientRect();
    let nextLeft = tabsContainer.scrollLeft;

    if (tabRect.right > containerRect.right) {
      nextLeft += tabRect.right - containerRect.right + 184;
    }

    if (nextLeft !== tabsContainer.scrollLeft) {
      tabsContainer.scrollTo({
        left: nextLeft,
        behavior: "smooth",
      });
    }
  }, [activeCategory, isMobile, categoriesForTabs]);

  const handleMobileCategoryTabClick = (category) => {
    setActiveCategory(category);
    setSelectedPinItem(null);

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <TitlePage>JAPAN TREND MAP</TitlePage>
      <FluidContent
        style={{ display: "flex" }}
        className={isMobile ? "" : "container-fluid"}
      >
        <StickyContainer className="stycky-content">
          <MainContent>
            {isMobile ? (
              <MobileListContainer>
                {categoriesForTabs.length > 0 && (
                  <>
                    <MobileStickyCategories data-fixed={isTabsFixed}>
                      <MobileCategoryTabs ref={mobileTabsRef}>
                        {categoriesForTabs.map((category) => (
                          <MobileCategoryTab
                            key={category.category}
                            ref={(node) => {
                              mobileTabRefs.current[category.category] = node;
                            }}
                            type="button"
                            data-active={activeCategory === category.category}
                            onClick={() => handleMobileCategoryTabClick(category.category)}
                          >
                            {category.category.toUpperCase()}
                          </MobileCategoryTab>
                        ))}
                      </MobileCategoryTabs>
                    </MobileStickyCategories>
                    {isTabsFixed && <div style={{ height: "70px", flexShrink: 0 }} />}
                  </>
                )}

                <MobileItemsScroll>
                  <MobileItemsList>
                    <MobileCategoryHeader>
                      <MobileCategoryLine />
                      <MobileCategoryTitle>
                        {activeCategory.toUpperCase()}
                        <MobileCategoryCount>
                          {activeMarkers.length} SPOTS
                        </MobileCategoryCount>
                      </MobileCategoryTitle>
                    </MobileCategoryHeader>

                    {activeMarkers.map((item, index) => {
                      const primaryImage = item.image1 || item.image2;
                      const isOpen = openIndexes.some(
                        (entry) =>
                          entry.index === index && entry.category === activeCategory
                      );

                      return (
                        <div key={`${activeCategory}-${item.title}-${index}`}>
                          <MobileItemCard data-open={isOpen}>
                            {primaryImage && (
                              <MobileItemImage data-open={isOpen}>
                                <img
                                  src={getImageFromDrive(primaryImage)}
                                  alt={`mobile_img_${index}`}
                                  onClick={() =>
                                    setSelectedImage(getImageFromDrive(primaryImage))
                                  }
                                />
                              </MobileItemImage>
                            )}

                            <MobileItemBody>
                              {!isOpen && (
                                <>
                                  <MobileItemTitleRow>
                                    <MobileItemIndex>{index + 1}</MobileItemIndex>
                                    <MobileItemTitle>{item.title}</MobileItemTitle>
                                  </MobileItemTitleRow>

                                  {item.bussiness && (
                                    <MobileItemBusiness style={{ color: item.color || "#9E9E9E" }}>
                                      {item.bussiness}
                                    </MobileItemBusiness>
                                  )}
                                </>
                              )}

                              <MobileItemActions data-open={isOpen}>
                                <small
                                  onClick={() => handleColapseItem(index, activeCategory)}
                                  role="button"
                                  tabIndex="0"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleColapseItem(index, activeCategory);
                                    }
                                  }}
                                >
                                  <BiSolidRightArrow
                                    style={{
                                      transform: `rotate(${isOpen ? "90deg" : "0deg"})`,
                                    }}
                                  />
                                  Detail
                                </small>
                              </MobileItemActions>
                            </MobileItemBody>
                          </MobileItemCard>

                          <Collapse className="collapse-content" isOpened={isOpen}>
                            <MobileDetailSection>
                              <MobileDetailMedia>
                                {(item.image1 || item.image2) && (
                                  <>
                                    <MobileDetailIndex>{index + 1}</MobileDetailIndex>
                                    <ImgContentItem>
                                      {item.image1 && (
                                        <ImgItem>
                                          <img
                                            src={getImageFromDrive(item.image1)}
                                            alt={"img1_" + item.index}
                                            onClick={() =>
                                              setSelectedImage(getImageFromDrive(item.image1))
                                            }
                                          />
                                        </ImgItem>
                                      )}
                                      {item.image2 && (
                                        <ImgItem>
                                          <img
                                            src={getImageFromDrive(item.image2)}
                                            alt={"img2_" + item.index}
                                            onClick={() =>
                                              setSelectedImage(getImageFromDrive(item.image2))
                                            }
                                          />
                                        </ImgItem>
                                      )}
                                    </ImgContentItem>
                                  </>
                                )}
                              </MobileDetailMedia>

                              <MobileDetailText>
                                <MobileItemTitle>{item.title}</MobileItemTitle>
                                {item.bussiness && (
                                  <MobileItemBusiness style={{ color: item.color || "#9E9E9E" }}>
                                    {item.bussiness}
                                  </MobileItemBusiness>
                                )}
                              </MobileDetailText>

                              <DescriptionItem>
                                {item.description
                                  ?.split(/\b(https?:\S+)\b/g)
                                  .map((parte, descriptionIndex) => {
                                    if (parte.match(/\bhttps?:\S+/)) {
                                      return (
                                        <a
                                          key={descriptionIndex}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          href={parte}
                                        >
                                          {parte}
                                        </a>
                                      );
                                    }

                                    return <span key={descriptionIndex}>{parte}</span>;
                                  })}
                              </DescriptionItem>

                              <MobileItemActions>
                                <small
                                  onClick={() => handleColapseItem(index, activeCategory)}
                                  role="button"
                                  tabIndex="0"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleColapseItem(index, activeCategory);
                                    }
                                  }}
                                >
                                  Detail
                                </small>
                              </MobileItemActions>
                            </MobileDetailSection>
                          </Collapse>
                        </div>
                      );
                    })}
                  </MobileItemsList>
                </MobileItemsScroll>
              </MobileListContainer>
            ) : (
              organizedData.map((category) => (
                <React.Fragment key={category.category}>
                  <TitleCategory>{category.category.toUpperCase()}</TitleCategory>
                  {category.data.map((item, index) => (
                    <ItemMap key={`${category.category}-${item.title}-${index}`}>
                      <TitleItem>
                        <b>
                          0{index + 1}. {item.title}{" "}
                          {item.bussiness && (
                            <span className="bussiness-tag" style={{ color: item.color || "#9E9E9E" }}>
                              {item.bussiness}
                            </span>
                          )}
                        </b>

                        <div className="detail-buttons">
                          <span
                            onClick={() => handleButtonClick(item.lat, item.lng)}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleButtonClick(item.lat, item.lng);
                              }
                            }}
                          >
                            MAP
                          </span>
                          <small
                            onClick={() => handleColapseItem(index, category.category)}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleColapseItem(index, category.category);
                              }
                            }}
                          >
                            <BiSolidRightArrow
                              style={{
                                transform: `rotate(${openIndexes.some(
                                  (entry) =>
                                    entry.index === index &&
                                    entry.category === category.category
                                )
                                  ? "90deg)"
                                  : "0deg)"
                                  }`,
                              }}
                            />{" "}
                            Detail
                          </small>
                        </div>
                      </TitleItem>
                      <Collapse
                        className="collapse-content"
                        isOpened={openIndexes.some(
                          (entry) =>
                            entry.index === index &&
                            entry.category === category.category
                        )}
                      >
                        <DescriptionItem>
                          {item.description
                            ?.split(/\b(https?:\S+)\b/g)
                            .map((parte, descriptionIndex) => {
                              if (parte.match(/\bhttps?:\S+/)) {
                                return (
                                  <a
                                    key={descriptionIndex}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={parte}
                                  >
                                    {parte}
                                  </a>
                                );
                              } else {
                                return <span key={descriptionIndex}>{parte}</span>;
                              }
                            })}
                        </DescriptionItem>

                        {item.image1 || item.image2 ? (
                          <ImgContentItem>
                            {item.image1 && (
                              <ImgItem>
                                <img
                                  src={getImageFromDrive(item.image1)}
                                  alt={"img1_" + item.index}
                                  onClick={() =>
                                    setSelectedImage(getImageFromDrive(item.image1))
                                  }
                                />
                              </ImgItem>
                            )}
                            {item.image2 && (
                              <ImgItem>
                                <img
                                  src={getImageFromDrive(item.image2)}
                                  alt={"img2_" + item.index}
                                  onClick={() =>
                                    setSelectedImage(getImageFromDrive(item.image2))
                                  }
                                />
                              </ImgItem>
                            )}
                          </ImgContentItem>
                        ) : (
                          ""
                        )}
                      </Collapse>
                    </ItemMap>
                  ))}
                </React.Fragment>
              ))
            )}
          </MainContent>

          <MapContent className="sticky-map">
            {isMobile ? (
              <MapLoader
                location={mobileMapCenter}
                zoom={zoom}
                animation={animation}
                isMobile={isMobile}
                markers={activeMarkers}
                markersKey={activeCategory}
                onMarkerClick={handleMarkerClick}
                onMapLoad={handleMapLoad}
              />
            ) : (
              <Sticky className="container-map" topOffset={80}>
                {({ style }) => (
                  <div style={style}>
                    <MapLoader
                      location={location}
                      zoom={zoom}
                      animation={animation}
                      isMobile={isMobile}
                      markers={desktopMarker}
                      markersKey="desktop"
                      onMapLoad={() => {}}
                    />
                  </div>
                )}
              </Sticky>
            )}
          </MapContent>
        </StickyContainer>

        {selectedPinItem && isMobile && (
          <MobilePinModalOverlay
            key={`pin-modal-${selectedPinItem.index}-${selectedPinItem.title}`}
            onClick={handleClosePinModal}
          >
            <MobilePinModalContent onClick={(e) => e.stopPropagation()}>
              <MobilePinModalHandle />
              <MobilePinModalHeader>
                <MobilePinModalTitleGroup>
                  <MobilePinModalIndex>{selectedPinItem.index + 1}</MobilePinModalIndex>
                  <MobilePinModalTitle>{selectedPinItem.title}</MobilePinModalTitle>
                  {selectedPinItem.bussiness && (
                    <MobilePinModalBusiness style={{ color: selectedPinItem.color || "#9E9E9E" }}>
                      {selectedPinItem.bussiness}
                    </MobilePinModalBusiness>
                  )}
                </MobilePinModalTitleGroup>
                <MobilePinModalClose
                  type="button"
                  onClick={handleClosePinModal}
                  aria-label="Close pin detail"
                >
                  <BiX />
                </MobilePinModalClose>
              </MobilePinModalHeader>

           

              <MobilePinModalDescription>
                <DescriptionItem>
                  {selectedPinItem.description
                    ?.split(/\b(https?:\S+)\b/g)
                    .map((parte, descriptionIndex) => {
                      if (parte.match(/\bhttps?:\S+/)) {
                        return (
                          <a
                            key={descriptionIndex}
                            target="_blank"
                            rel="noopener noreferrer"
                            href={parte}
                          >
                            {parte}
                          </a>
                        );
                      }

                      return <span key={descriptionIndex}>{parte}</span>;
                    })}
                </DescriptionItem>
              </MobilePinModalDescription>
            </MobilePinModalContent>
          </MobilePinModalOverlay>
        )}

        {selectedImage && (
          <ImageModalOverlay onClick={() => setSelectedImage("")}>
            <ImageModalContent onClick={(e) => e.stopPropagation()}>
              <ImageModalClose
                type="button"
                onClick={() => setSelectedImage("")}
                aria-label="Close image"
              >
                <BiX />
              </ImageModalClose>
              <img src={selectedImage} alt="selected_map" />
            </ImageModalContent>
          </ImageModalOverlay>
        )}
      </FluidContent>
    </>
  );
};


export default TokyoMaps;
